import { noCacheUrl } from 'components/VisualStudioPlayground/Shapes/ImageShape/helpers'
import { getImageSizeFromSrc } from './getImageSizeFromSrc'
import { AssetRecordType, Editor, TLAssetId, TLImageAsset, createShapeId } from '@tldraw/tldraw'
import {
  findAssetByTransformations,
  getCurrentFrame,
  getReplaceableImageShapes,
} from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { IMediaFile } from './Types'
import { fitAndPosition } from 'object-fit-math'
import { ImageShape } from 'components/VisualStudioPlayground/Shapes/ImageShape/Types'
import { ImageShapeUtil } from 'components/VisualStudioPlayground/Shapes/ImageShape/ImageShapeUtil'
import { MediaVariant } from 'types'
import { MaskShape } from 'components/VisualStudioPlayground/Shapes/MaskShape/Types'
import { MaskShapeUtil } from 'components/VisualStudioPlayground/Shapes/MaskShape/MaskShapeUtil'

const FRAME_PADDING = 0.2

/**
 * Create the asset for a media file, without any transformations.
 *
 * @param file The media file to create an asset for
 */
export const createOriginalAssetFromFile = async (file: IMediaFile) => {
  let dimensions = file.width && file.height ? { width: file.width, height: file.height } : undefined
  if (!dimensions) {
    const imageSize = await getImageSizeFromSrc(noCacheUrl(file.source_url))
    dimensions = { width: imageSize.w, height: imageSize.h }
  }
  return AssetRecordType.create({
    type: 'image',
    typeName: 'asset',
    meta: {
      mediaFileId: file.id,
      transformations: {},
    },
    props: {
      name: file.source_url,
      src: file.source_url,
      w: dimensions.width,
      h: dimensions.height,
      mimeType: file.mime_type,
      isAnimated: false,
    },
  }) as TLImageAsset
}

/**
 * Create an asset for a media variant.
 *
 * @param mediaFileId The media file ID
 * @param variant The media variant to create an asset for
 */
export const createVariantAsset = (mediaFileId: string, variant: MediaVariant) => {
  const id = `asset:${mediaFileId}-${variant.digest}` as TLAssetId
  return AssetRecordType.create({
    id,
    type: 'image',
    typeName: 'asset',
    meta: {
      mediaFileId,
      transformations: JSON.parse(JSON.stringify(variant.transformations)),
    },
    props: {
      name: variant.source_url,
      src: variant.source_url,
      w: variant.width,
      h: variant.height,
      mimeType: variant.mime_type,
      isAnimated: false,
    },
  }) as TLImageAsset
}

/**
 * Create assets from a media file, including the original asset and an asset
 * for each variant. If a variant does not have a source URL, it will be
 * skipped.
 *
 * @param file The media file to create assets from
 */
export const createAssetsFromFile = async (file: IMediaFile) => {
  const originalAsset = await createOriginalAssetFromFile(file)
  const variants = file.variants?.filter((variant): variant is MediaVariant => variant.source_url !== null) || []
  return [originalAsset, ...variants.map((variant) => createVariantAsset(file.id, variant))]
}

/**
 * Create an image shape from a media file.
 * The media file must contain the variant with remove_background if removeBackground is true.
 * If a replaceable image shape is selected, it will be replaced with the new image.
 * If a replaceable image shape is present on the canvas, it will be replaced with the new image.
 *
 * @param editor The editor instance
 * @param file The media file to insert
 * @param options Options for inserting the image
 * @param options.removeBackground Whether to remove the background
 * @param options.replaceReplaceable Whether to replace any replaceable image shape
 * @param options.replaceReplaced Whether replaced images can be replaced
 * @param options.existingShape The shape to replace
 * @param options.isLogo Whether the image is a logo
 * @param options.w The width of the image
 * @param options.h The height of the image
 */
export const insertImage = async (
  editor: Editor,
  file: IMediaFile,
  options: {
    removeBackground?: boolean
    existingShape?: ImageShape | MaskShape
    replaceReplaceable?: boolean
    replaceReplaced?: boolean
    isLogo?: boolean
    w?: number
    h?: number
    fit?: 'contain' | 'cover'
  } = {},
) => {
  const {
    removeBackground = false,
    replaceReplaceable = false,
    isLogo = false,
    w,
    h,
    existingShape,
    replaceReplaced = false,
    fit = 'contain',
  } = options

  editor.batch(async () => {
    const assets = await createAssetsFromFile(file)
    editor.createAssets(assets)
    const asset = findAssetByTransformations(
      editor,
      file.id,
      removeBackground ? { remove_background: { crop: true } } : {},
    )

    if (!asset) {
      return
    }

    const frame = getCurrentFrame(editor)

    if (existingShape) {
      if (existingShape.type === 'image') {
        const shapeUtil = editor.getShapeUtil<ImageShape>(existingShape) as ImageShapeUtil
        shapeUtil.replace(existingShape, asset.id, undefined, { fit })
        return
      } else {
        const shapeUtil = editor.getShapeUtil<MaskShape>(existingShape) as MaskShapeUtil
        shapeUtil.replace(existingShape, asset.id)
        return
      }
    }

    if (replaceReplaceable) {
      const replaceable = getReplaceableImageShapes(editor, replaceReplaced).find((shape) => {
        return editor.getShapeParent(shape)?.id === frame.id
      })
      if (replaceable?.type === 'image') {
        const util = editor.getShapeUtil<ImageShape>(replaceable) as ImageShapeUtil
        util.replace(replaceable, asset.id, { removeBackground: removeBackground ? { crop: true } : null }, { fit })
        return
      }
      if (replaceable?.type === 'mask' && asset.props.mimeType !== 'image/svg+xml') {
        const util = editor.getShapeUtil<MaskShape>(replaceable) as MaskShapeUtil
        util.replace(replaceable, asset.id)
        return
      }
    }

    const onlySelectedShape = editor.getOnlySelectedShape()
    const onlySelectedImage: ImageShape | undefined =
      onlySelectedShape?.type === 'image' ? (onlySelectedShape as ImageShape) : undefined

    if (onlySelectedImage) {
      const util = editor.getShapeUtil<ImageShape>(onlySelectedImage) as ImageShapeUtil
      if (util.canBeReplaced(onlySelectedImage)) {
        util.replace(
          onlySelectedImage,
          asset.id,
          { removeBackground: removeBackground ? { crop: true } : null },
          { fit },
        )
        return
      }
    }

    const fittedImage = fitAndPosition(
      { width: frame.props.w * (1 - FRAME_PADDING), height: frame.props.h * (1 - FRAME_PADDING) },
      { width: asset.props.w, height: asset.props.h },
      fit,
    )
    editor.createShape({
      id: createShapeId(),
      type: 'image',
      parentId: frame.id,
      x: frame.props.w / 2 - fittedImage.width / 2,
      y: frame.props.h / 2 - fittedImage.height / 2,
      props: {
        assetId: asset.id,
        w: w || fittedImage.width,
        h: h || fittedImage.height,
        removeBackground: removeBackground ? { crop: true } : null,
        isLogo,
      },
    })
  })
}
